import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useHistory, useParams } from "react-router-dom";
import { toast } from "sonner";

import FastAPI from "../../api/FastAPI";

import type { UseMutationResult } from "@tanstack/react-query";

import type {
  GetTemplateResponse,
  UpdatedTemplate,
} from "../../api/FastAPI.types";
import type { FASTChannelRoutesParams } from "../../components/routes/FASTChannelRoutes";

interface MutationParams {
  isNewTemplate: boolean;
  updatedTemplate: UpdatedTemplate;
}

interface UseFASTTemplateMutationsParams {
  updateFASTTemplate: UseMutationResult<
    GetTemplateResponse,
    unknown,
    MutationParams
  >;
}

function useFASTTemplateMutations(): UseFASTTemplateMutationsParams {
  const history = useHistory();
  const { t } = useTranslation("editor-fast-channel");

  const { channelId, templateId } = useParams<
    FASTChannelRoutesParams & {
      templateId: string;
    }
  >();
  const queryClient = useQueryClient();

  const getTemplatesListQuery = FastAPI.generateQuery({
    fn: FastAPI.getTemplatesList,
    params: [channelId],
  });

  const getTemplateQuery = FastAPI.generateQuery({
    fn: FastAPI.getTemplate,
    params: [channelId, templateId],
  });

  const updateFASTTemplate = useMutation({
    mutationFn: async ({ isNewTemplate, updatedTemplate }: MutationParams) =>
      isNewTemplate
        ? FastAPI.createTemplate({
            channelId,
            data: updatedTemplate,
          })
        : FastAPI.updateTemplate({
            channelId,
            templateId,
            data: updatedTemplate,
          }),
    onMutate: ({
      isNewTemplate,
      updatedTemplate,
    }): GetTemplateResponse | undefined => {
      if (isNewTemplate) return undefined;

      const previousTemplate = queryClient.getQueryData<GetTemplateResponse>(
        getTemplateQuery.queryKey,
      );

      queryClient.setQueryData(getTemplateQuery.queryKey, {
        ...previousTemplate,
        ...updatedTemplate,
      });

      return previousTemplate;
    },
    onSuccess: ({ uuid }, { isNewTemplate }): void => {
      toast.success(
        isNewTemplate ? t("templateCreateSuccess") : t("templateUpdateSuccess"),
      );

      if (isNewTemplate) {
        history.replace(uuid);
      }

      queryClient.invalidateQueries(getTemplatesListQuery.queryKey);
    },
    onError: (_, { isNewTemplate }, previousTemplate): void => {
      if (!isNewTemplate) {
        queryClient.setQueryData(getTemplateQuery.queryKey, previousTemplate);
      }
      toast.error(
        isNewTemplate ? t("templateCreateError") : t("templateUpdateError"),
      );
    },
  });

  return {
    updateFASTTemplate,
  };
}

export default useFASTTemplateMutations;
